import React from 'react';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { styled } from '@mui/material/styles';
import { Divider } from '@mui/material';

import { Avatar, theme, SingleLineText } from '@bloomays-lib/ui.shared';

export type buttonSize = 'large' | 'medium' | 'small';
export type AvatarUsernameProps = {
  name: string;
  jobTitle?: string;
  image?: string;
  size: buttonSize;
  notSeen?: boolean;
  topProfile?: boolean;
  caption?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

const ProfileCard = ({ name, jobTitle, image, size, onClick, notSeen, topProfile, caption }: AvatarUsernameProps) => {
  return (
    <ProfileContainer onClick={onClick}>
      <ProfileWrapper>
        <Profile>
          <Avatar image={image} size={size} alt={name} />
          <StyledDiv>
            <SingleLineText variant={size === 'medium' ? 'body2Medium' : 'subtitle2Medium'} text={name} />
            {jobTitle && <SingleLineText variant={size === 'medium' ? 'caption' : 'body1Light'} text={jobTitle} />}
          </StyledDiv>
        </Profile>
        {caption && (
          <>
            <Divider orientation="horizontal" flexItem />
            <SingleLineText text={caption} variant="caption" />
          </>
        )}
      </ProfileWrapper>
      {notSeen && <IconNotSeen />}
      {topProfile && <IconTopProfile />}
    </ProfileContainer>
  ) as React.ReactNode;
};

export default ProfileCard;
export { ProfileCard };

const ProfileContainer = styled('div')<{ onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void }>(
  ({ theme, onClick }) => ({
    backgroundColor: theme.palette.neutral.white,
    padding: '10px',
    display: 'flex',
    gap: '10px',
    justifyContent: 'space-between',
    position: 'relative',
    alignItems: 'center',
    '&:hover': {
      cursor: onClick ? 'pointer' : 'auto',
    },
  }),
);

const StyledDiv = styled('div')(() => ({
  textAlign: 'left',
}));

const IconNotSeen = styled('div')(() => ({
  position: 'absolute',
  width: '10px',
  height: '10px',
  backgroundColor: theme.palette.red[60],
  borderRadius: '50%',
  top: '8px',
  right: '8px',
}));

const IconTopProfile = styled(WhatshotIcon)(() => ({
  width: '32px',
  height: '32px',
  color: theme.palette.purple[100],
}));

const ProfileWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
}));

const Profile = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
}));
